import { useCallback, useEffect, useRef, useState } from 'react';

type UseTimelinePlayheadOptions = {
  segmentMs?: number;
  threshold?: number;
};

export function useTimelinePlayhead(
  itemCount: number,
  { segmentMs = 5000, threshold = 0.25 }: UseTimelinePlayheadOptions = {},
) {
  const sectionRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);
  const [inView, setInView] = useState(false);
  const [cycleKey, setCycleKey] = useState(0);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const totalDuration = segmentMs * itemCount;

  const clearSweep = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const startSweep = useCallback(() => {
    clearSweep();
    if (itemCount <= 0) return;

    setActive(0);

    for (let i = 1; i < itemCount; i += 1) {
      const t = setTimeout(() => setActive(i), segmentMs * i);
      timeoutsRef.current.push(t);
    }

    const loopT = setTimeout(() => {
      setCycleKey((k) => k + 1);
    }, totalDuration);
    timeoutsRef.current.push(loopT);
  }, [clearSweep, itemCount, segmentMs, totalDuration]);

  useEffect(() => {
    if (cycleKey === 0) return;
    startSweep();
  }, [cycleKey, startSweep]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            setCycleKey((k) => k + 1);
          } else {
            setInView(false);
            clearSweep();
          }
        });
      },
      { threshold },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      clearSweep();
    };
  }, [threshold, clearSweep]);

  return {
    sectionRef,
    active,
    inView,
    segmentMs,
    totalDuration,
    cycleKey,
  };
}
